/** Centralised token key constants — single source of truth across the app. */
export const TOKEN_KEYS = {
  ACCESS: "accessToken",
  REFRESH: "refreshToken",
  USERNAME: "username",
  SESSION: "session",
} as const;

// ---------------------------------------------------------------------------
// JWT helpers
// ---------------------------------------------------------------------------

/**
 * Decode the payload section of a JWT without any external library.
 * Returns `null` if the token is malformed or the payload cannot be parsed.
 */
export const decodeJwtPayload = (
  token: string,
): Record<string, unknown> | null => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    // base64url → base64 → JSON
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/**
 * Returns `true` when the token is already expired **or** will expire within
 * `bufferSeconds` (default 30 s).  Treats malformed / missing tokens as expired.
 */
export const isTokenExpired = (token: string, bufferSeconds = 30): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return Date.now() / 1000 >= payload.exp - bufferSeconds;
};

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

export interface StoredTokens {
  accessToken: string | null;
  refreshToken: string | null;
  username: string | null;
}

/** Read all auth-related values from localStorage in one call. */
export const getStoredTokens = (): StoredTokens => ({
  accessToken: localStorage.getItem(TOKEN_KEYS.ACCESS),
  refreshToken: localStorage.getItem(TOKEN_KEYS.REFRESH),
  username: localStorage.getItem(TOKEN_KEYS.USERNAME),
});

export interface StoreTokensInput {
  accessToken: string;
  refreshToken?: string;
  username?: string;
}

/**
 * Persist tokens to localStorage.
 * `refreshToken` and `username` are only written when provided so that
 * a partial update (e.g. access-token-only rotation) does not wipe existing values.
 */
export const storeTokens = ({
  accessToken,
  refreshToken,
  username,
}: StoreTokensInput): void => {
  localStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
  if (refreshToken !== undefined) {
    localStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken);
  }
  if (username !== undefined) {
    localStorage.setItem(TOKEN_KEYS.USERNAME, username);
  }
};

/** Remove every auth-related key from localStorage (used on logout / session expiry). */
export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEYS.ACCESS);
  localStorage.removeItem(TOKEN_KEYS.REFRESH);
  localStorage.removeItem(TOKEN_KEYS.USERNAME);
  localStorage.removeItem(TOKEN_KEYS.SESSION);
};
